/**
 * Project list: the entry point after signing in.
 *
 * Each row carries enough of a summary that the right project is obvious without opening it,
 * and hovering or touching a row warms that project's derived statistics before the
 * navigation happens.
 */

import { useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { formatRate } from '../../shared/stats.js';
import type { Project } from '../../shared/types.js';
import { createProject } from '../data/mutations.js';
import { useStore } from '../data/context.js';
import { useProjectDerived } from '../data/derived.js';
import { usePredictiveWarm, useWarmHandlers } from '../data/prefetch.js';
import { useProjects } from '../data/selectors.js';
import { useSession } from '../auth/session.js';
import { StateLegend } from '../ui/attendance.js';
import { StatusBar } from '../ui/charts/StatusBar.js';
import { plural, relativeDayLabel } from '../ui/format.js';
import { ChevronRightIcon, PlusIcon } from '../ui/icons.js';
import { Button, Dialog, EmptyState, TextField } from '../ui/primitives.js';
import { PlainFrame } from '../ui/Shell.js';

/** Renders one project as a row with its headline numbers. */
function ProjectRow({ project }: { project: Project }): ReactNode {
  const derived = useProjectDerived(project.id);
  const warm = useWarmHandlers(project.id);

  if (derived === null) return null;
  const { summary, lastSession } = derived;

  return (
    <li className="border-b border-line bg-surface">
      <Link to={`/p/${project.id}`} className="block px-4 py-4 transition-colors hover:bg-sunken" {...warm}>
        <div className="flex items-center gap-4">
          <div className="min-w-0 flex-1">
            <p className="truncate text-title font-semibold">{project.name}</p>
            <p className="tabular mt-0.5 truncate text-tiny text-ink-2">
              {summary.rosterSize} {plural(summary.rosterSize, 'person', 'people')} ·{' '}
              {summary.sessionCount} {plural(summary.sessionCount, 'session', 'sessions')}
              {lastSession === null ? '' : ` · last session ${(relativeDayLabel(lastSession, derived.day) ?? 'earlier').toLowerCase()}`}
            </p>
          </div>
          <div className="shrink-0 text-right">
            <p className="tabular text-title font-bold text-brand-text">{formatRate(summary.rate)}</p>
            <p className="text-tiny text-ink-3">present</p>
          </div>
          <ChevronRightIcon size={18} />
        </div>
        <div className="mt-3"><StatusBar counts={summary.counts} height={6} /></div>
      </Link>
    </li>
  );
}

/** Form for naming a new project. */
function NewProjectDialog({
  onClose,
  onCreate,
}: {
  onClose: () => void;
  onCreate: (name: string) => void;
}): ReactNode {
  const [name, setName] = useState('');
  return (
    <Dialog
      title="New project"
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button tone="primary" disabled={name.trim() === ''} onClick={() => { onCreate(name); onClose(); }}>
            Create
          </Button>
        </>
      }
    >
      <TextField
        label="Name"
        value={name}
        onChange={setName}
        autoFocus
        placeholder="Maths 9b, Tuesday choir, …"
      />
    </Dialog>
  );
}

/** Renders the project list. */
export function ProjectsPage(): ReactNode {
  const store = useStore();
  const projects = useProjects();
  const { session } = useSession();
  const [creating, setCreating] = useState(false);

  const projectIds = useMemo(() => projects.map((project) => project.id), [projects]);
  usePredictiveWarm(projectIds);

  const newButton = (
    <Button tone="primary" onClick={() => setCreating(true)}>
      <PlusIcon size={18} />
      New project
    </Button>
  );

  return (
    <PlainFrame title="Anwesenheit" {...(projects.length === 0 ? {} : { action: newButton })}>
      {projects.length === 0 ? (
        <EmptyState
          message="No projects yet. A project is one group whose attendance you check — a class, a team, a course."
          action={newButton}
        />
      ) : (
        <>
          <ul className="border-t border-line">
            {projects.map((project) => <ProjectRow key={project.id} project={project} />)}
          </ul>
          {/* Each row carries a distribution bar and no figures, so the colours are named once. */}
          <div className="px-5 py-3"><StateLegend /></div>
        </>
      )}

      {creating ? (
        <NewProjectDialog
          onClose={() => setCreating(false)}
          onCreate={(name) => void createProject(store, name, session?.email ?? '')}
        />
      ) : null}
    </PlainFrame>
  );
}
